import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'store';
import useAuthHook from 'hooks/modules/useAuthHook';
import useLocationHook from 'hooks/modules/mdm/useLocationHook';
import AuthService from 'services/AuthService';
import {
  setUserLocations,
  setUserLocationsLoading
} from 'store/reducers/userLocationSlice';
import {
  getAssignedDistrictIds,
  getAssignedDistrictLocations,
  getAssignedProvinceLocations,
  getDistrictAccessMode,
  getDistrictLocations,
  getDistrictNames,
  getProvinceLocations,
  isAdministrator
} from 'helpers/districtAccess';

/** Extract district IDs from a profile's `location_ids`/`locations`, tolerating
 *  shapes: number[], [{ location_id }], or [{ id }] (location objects). */
const normalizeLocationIds = (payload: unknown): number[] => {
  const rows = Array.isArray(payload) ? payload : [];
  return rows
    .map(row => {
      if (typeof row === 'number') return row;
      const r = row as { location_id?: number; id?: number };
      return r?.location_id ?? r?.id;
    })
    .filter((value): value is number => typeof value === 'number');
};

const extractProfileLocationIds = (profile: unknown): number[] => {
  const p = profile as { location_ids?: unknown; locations?: unknown };
  if (Array.isArray(p?.location_ids))
    return normalizeLocationIds(p.location_ids);
  if (Array.isArray(p?.locations)) return normalizeLocationIds(p.locations);
  return [];
};

// Module-level in-flight guard. The hook is consumed by many components that
// mount in the same tick; each reads `loading: false` from that render's snapshot
// before any dispatch re-renders them, so a Redux flag alone can't dedupe the
// concurrent effects. This guard ensures only one profile request is in flight.
let profileFetchInFlight = false;

/**
 * Single source of truth for the logged-in user's district scope (Spec #2a).
 *
 * The scope is fetched from `GET /api/auth/tenants/myProfile/get` and cached in
 * the `userLocation` slice — it is no longer read from the auth token. Falls back
 * to the token's `location_ids` only when no user is authenticated.
 */
const useDistrictAccessHook = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useAuthHook();
  const { locations } = useLocationHook();
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  const { locationIds, loading, loaded } = useSelector(
    state => state.userLocation
  );

  // Fetch (once per session) the logged-in user's profile, which carries their
  // assigned district location_ids. Reset on logout re-triggers it for the next user.
  useEffect(() => {
    if (!isAuthenticated || loading || loaded || profileFetchInFlight) return;

    profileFetchInFlight = true;
    dispatch(setUserLocationsLoading({ userId: null }));
    AuthService.getProfile()
      .then(resp => {
        const profile = resp?.data?.data ?? resp?.data ?? {};
        dispatch(
          setUserLocations({
            userId: (profile as { id?: number })?.id ?? null,
            locationIds: extractProfileLocationIds(profile)
          })
        );
      })
      .catch(e => {
        console.log('fetchMyProfile', e);
        // Fall back to the token's location_ids if the profile call fails.
        dispatch(
          setUserLocations({
            userId: null,
            locationIds: getAssignedDistrictIds(user)
          })
        );
      })
      .finally(() => {
        profileFetchInFlight = false;
      });
  }, [isAuthenticated, loaded, loading, user, dispatch]);

  return useMemo(() => {
    // API-sourced scope when authenticated; otherwise fall back to the token.
    const effectiveIds = isAuthenticated
      ? locationIds
      : getAssignedDistrictIds(user);
    const scopeReady = isAuthenticated ? loaded : true;

    const scopedUser = {
      user_role: user?.user_role,
      location_ids: effectiveIds
    };

    const mode = getDistrictAccessMode(scopedUser);
    const assignedDistrictIds = getAssignedDistrictIds(scopedUser);

    const allDistricts = getDistrictLocations(locations);
    const allProvinces = getProvinceLocations(locations);
    const assignedDistricts = getAssignedDistrictLocations(
      locations,
      scopedUser
    );
    const assignedProvinces = getAssignedProvinceLocations(
      locations,
      scopedUser
    );

    // What the user is allowed to pick from, given their access mode.
    const availableDistricts =
      mode === 'full' ? allDistricts : assignedDistricts;
    const availableProvinces =
      mode === 'full' ? allProvinces : assignedProvinces;

    // The province/district a single-district user is implicitly scoped to.
    // Listings seed this before their first fetch so the request goes out once,
    // already carrying the district (avoids an unscoped-then-scoped double load).
    const scopedDistrictId = mode === 'single' ? effectiveIds[0] : undefined;
    const scopedDistrict = scopedDistrictId
      ? assignedDistricts.find(d => d.id === scopedDistrictId)
      : undefined;
    const defaultLocationFilter = scopedDistrictId
      ? {
          province_id: (scopedDistrict?.parent_id ?? undefined) as
            | number
            | undefined,
          district_id: scopedDistrictId
        }
      : {};

    return {
      user,
      mode,
      isAdmin: isAdministrator(scopedUser),
      isFull: mode === 'full',
      isSingle: mode === 'single',
      isMulti: mode === 'multi',
      assignedDistrictIds,
      allDistricts,
      allProvinces,
      assignedDistricts,
      assignedProvinces,
      availableDistricts,
      availableProvinces,
      defaultLocationFilter,
      scopeReady,
      getDistrictNames: (ids: number[]) =>
        getDistrictNames(locations, ids, isEnglish)
    };
  }, [user, isAuthenticated, locationIds, loaded, locations, isEnglish]);
};

export default useDistrictAccessHook;
